import { type InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import { debounce } from "es-toolkit/compat";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";

import { useTranslation } from "@/core/i18n";
import { getMultiPlatformLocations } from "api/methods";
import type { GetResult } from "api/types";
import { DraggableModal } from "atoms/draggable-modal/DraggableModal";
import ChevronDown from "atoms/icon/icons/chevron_down_small.svg";
import Close from "atoms/icon/icons/close.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import { SearchFieldV3 } from "atoms/search-field/SearchFieldV3";
import { Spinner } from "atoms/Spinner";
import { TextButton } from "atoms/text-button/TextButton";

import { infiniteQueryKey } from "api/queryKeys";

type LocationSearchResult = GetResult<"/multi-platform/locations">;
type LocationItem = NonNullable<LocationSearchResult["items"]>[0];

interface PlatformSearchModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onPlatformSelect: (platformId: string) => void | Promise<void>;
  readonly canClose?: boolean; // If false, modal cannot be closed
  // Platforms the signed-in user already belongs to; hidden from results so they
  // cannot re-onboard into a practice they are already a member of.
  readonly excludedPlatformIds?: readonly string[];
}

const getSearchResults = (pages: InfiniteData<LocationSearchResult>["pages"] = []) =>
  pages.reduce((accumulator, page) => accumulator.concat(page.items || []), [] as LocationItem[]) ||
  [];

// Group locations by platform
const groupByPlatform = (locations: LocationItem[]) => {
  const platformMap = new Map<
    string,
    {
      platformId: string;
      platformName: string;
      platformImageUrl: string | null;
      locationCount: number;
    }
  >();

  for (const location of locations) {
    const platformId = location.platformId;
    if (!platformId) continue;

    if (!platformMap.has(platformId)) {
      platformMap.set(platformId, {
        platformId,
        platformName: location.platformName || "",
        platformImageUrl: location.platformImageUrl || null,
        locationCount: 1,
      });
    } else {
      const platform = platformMap.get(platformId);
      if (platform) {
        platform.locationCount++;
      }
    }
  }

  return Array.from(platformMap.values());
};

export function PlatformSearchModal({
  open,
  onClose,
  onPlatformSelect,
  canClose = true,
  excludedPlatformIds = [],
}: PlatformSearchModalProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const {
    data: searchResults,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    initialPageParam: 1,
    retry: false,
    enabled: !!debouncedSearchQuery && open,
    staleTime: Infinity,
    queryKey: infiniteQueryKey(
      getMultiPlatformLocations.key({ searchQuery: debouncedSearchQuery, mileRadius: 50 }),
    ),
    queryFn: ({ pageParam = 1 }) =>
      getMultiPlatformLocations({
        page: pageParam,
        searchQuery: debouncedSearchQuery,
        mileRadius: 50,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.totalPages > lastPage.page) {
        return lastPage.page + 1;
      }
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page !== 1) {
        return firstPage.page - 1;
      }
    },
  });

  const updateDebouncedSearch = useRef(
    debounce((query) => setDebouncedSearchQuery(query), 700),
  ).current;

  useEffect(() => {
    updateDebouncedSearch(searchQuery);
  }, [searchQuery, updateDebouncedSearch]);

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    }
  };

  const handlePlatformSelect = async (platformId: string) => {
    await onPlatformSelect(platformId);
    if (canClose) {
      onClose();
    }
  };

  const handleModalClose = () => {
    // Only allow closing if canClose is true
    if (canClose) {
      onClose();
    }
  };

  const allSearchResults = getSearchResults(searchResults?.pages);
  const excludedPlatformIdSet = new Set(excludedPlatformIds);
  const platforms = groupByPlatform(allSearchResults).filter(
    (platform) => !excludedPlatformIdSet.has(platform.platformId),
  );
  const resultCount = platforms.length;

  return (
    <DraggableModal
      open={open}
      onClose={handleModalClose}
      canClose={canClose}
      size="h-[min(75vh,calc(100vh-env(safe-area-inset-top)-180px))] desk:h-auto"
      header={
        <div className="flex h-[69px] w-full shrink-0 border-b border-light-grey">
          {canClose && (
            <div className="pl-3.5 pt-3">
              <IconButton svg={Close} size="small" style="iconOnly" onClick={handleModalClose} />
            </div>
          )}
          <div
            className={`flex w-full items-center justify-center pb-[24px] pt-[26px] font-sans text-bold2 uppercase tracking-[.04em] text-primary ${
              canClose ? "pr-[55px]" : ""
            }`}
          >
            {t("platformSearchModal.title")}
          </div>
        </div>
      }
      bodyClassName="p-4"
    >
      <div className="flex flex-col gap-4">
        <SearchFieldV3
          placeholder={t("platformSearchModal.searchPlaceholder")}
          autoFocus
          label={t("platformSearchModal.searchLabel")}
          name="search_query"
          value={searchQuery}
          onChange={setSearchQuery}
          onKeyDown={handleInputKeyDown}
          onIconClick={(side) => side === "right" && setSearchQuery("")}
        />

        {isFetching && !allSearchResults.length ? (
          <div className="mt-10 flex flex-col items-center justify-center gap-6">
            <Spinner />
            <div className="text-body2 text-secondary">
              {t("platformSearchModal.searchingPractices")}
            </div>
          </div>
        ) : (
          <div>
            {!!searchQuery && (
              <div className="mb-2 text-body2 text-secondary">
                {resultCount === 1
                  ? t("platformSearchModal.resultCountOne", { count: resultCount })
                  : t("platformSearchModal.resultCountOther", { count: resultCount })}
              </div>
            )}

            {!!searchQuery && !platforms.length && !isFetching && (
              <div className="mt-10 flex flex-col items-center justify-center text-center">
                <div className="mb-2 text-body1">{t("common.noResults")}</div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {platforms.map((platform) => (
                <button
                  key={platform.platformId}
                  onClick={() => handlePlatformSelect(platform.platformId)}
                  className="flex items-center gap-4 rounded-xl bg-white p-4 shadow-sm transition-all hover:shadow-md"
                >
                  {/* Platform logo */}
                  <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white shadow-sm">
                    {platform.platformImageUrl ? (
                      <img
                        src={platform.platformImageUrl}
                        alt={platform.platformName}
                        className="size-full object-contain"
                      />
                    ) : (
                      <div className="flex size-full items-center justify-center bg-gradient-to-br from-brand-color to-[#4ECBC4] font-sans text-lg font-bold text-white">
                        {platform.platformName.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* Platform info */}
                  <div className="flex flex-1 flex-col items-start text-left">
                    <div className="font-sans text-body1 font-normal text-primary">
                      {platform.platformName}
                    </div>
                    <div className="text-body2 text-secondary">
                      {platform.locationCount === 1
                        ? t("platformSearchModal.locationCountOne", {
                            count: platform.locationCount,
                          })
                        : t("platformSearchModal.locationCountOther", {
                            count: platform.locationCount,
                          })}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {hasNextPage && (
              <div className="flex items-center justify-center py-4">
                <TextButton
                  disabled={isFetching}
                  onClick={() => fetchNextPage()}
                  endIconSVG={ChevronDown}
                >
                  {t("platformSearchModal.loadMoreResults")}
                </TextButton>
              </div>
            )}
          </div>
        )}
      </div>
    </DraggableModal>
  );
}
